import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable, firstValueFrom, filter, BehaviorSubject, from, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { ChannelI, ChannelE } from '@interfaces/channel.interface';
import { UserHelperService } from '@helpers/user-helper.service';
import { UserAttributesI } from '@interfaces/user.interface';
import { PageI } from '@interfaces/page.interface';
import { StatusE } from '@interfaces/flow.interface';
import { ChannelTypeE } from '@enums/channel.enum';
import { ChannelsSocketService } from '@events/channels/channels-socket.service';
import { ChangeOperationCollectionEventI } from '@interfaces/change-operation-collection-event.interface';

export interface RcsBrokerUsageI {
  providerBroker: string;
  customerId: string;
  customerName?: string;
  channelName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChannelsHttpService {
  private facebookUrl = environment.OMNISMART_API_FACEBOOK;
  private instagramUrl = environment.OMNISMART_API_INSTAGRAM_DIRECT;

  private chanelUrl = environment.OMNISMART_API_CHANNEL;

  private channelSlug: string = 'channels/';

  constructor(
    private readonly http: HttpClient,
    private readonly userHelperService: UserHelperService,
    private injector: Injector
  ) {}

  public initOnce() {
    const channelsSocketService = this.injector.get(ChannelsSocketService);
    if (!channelsSocketService) {
      return;
    }
    channelsSocketService.init();
    channelsSocketService.change$.subscribe((change) => {
      if (!change) {
        return;
      }
      this.treatCrudOperations(change);
    });
  }

  private async treatCrudOperations(change: ChangeOperationCollectionEventI<ChannelI>) {
    let channels: ChannelI[] | null = await firstValueFrom(this.channelsCache$);
    if (!channels) {
      //se esta null n precida alimentar o cache , pois será carregado por completo quando usado
      return;
    }
    const { operation, data } = change;
    if (!data) {
      return;
    }
    if (operation === 'delete') {
      const { _id } = data;
      const idx = channels.findIndex((row) => row._id === _id);
      if (idx !== -1) {
        channels.splice(idx, 1);
        this.channelsSubject.next(channels);
      }
    } else if (operation === 'update') {
      const { _id } = data;
      const idx = channels.findIndex((row) => row._id === _id);
      if (idx !== -1) {
        channels[idx] = data as ChannelI;
        this.channelsSubject.next(channels);
      }
    } else if (operation === 'create') {
      channels.push(data as ChannelI);
      this.channelsSubject.next(channels);
    }
  }

  public findAllChannels(
    search = '',
    page = 1,
    limit = 99999,
    active = 'name',
    sort = 'desc'
  ): Observable<PageI<ChannelI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString());

    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug, { params });
  }

  public availableInstances(type: ChannelTypeE): Observable<any> {
    let params = new HttpParams().set('type', type as string);
    return this.http.get(this.chanelUrl + this.channelSlug + 'total-instances-available', { params });
  }

  public createChannel(channel: ChannelI): Observable<ChannelI> {
    return this.http.post<ChannelI>(this.chanelUrl + this.channelSlug, channel);
  }

  public createChannelFacebook(token: string, customerId: string) {
    return this.http.get<ChannelI>(
      `${this.facebookUrl}` + `auth/facebook-messenger/process-data?token=` + token + `&customerId=` + customerId
    );
  }

  public createChannelInstagram(token: string, customerId: string) {
    return this.http.get<ChannelI>(
      `${this.instagramUrl}` + `auth/instagram-direct/process-data?token=` + token + `&customerId=` + customerId
    );
  }

  public getInternalChannelByCustomerId(): Observable<ChannelI> {
    return this.http.get<ChannelI>(this.chanelUrl + this.channelSlug + 'internal');
  }

  private channelsSubject = new BehaviorSubject<ChannelI[] | null>(null);

  private get channelsCache$() {
    return this.channelsSubject.asObservable();
  }

  /**
   * Agentes RCS já vinculados a algum canal da plataforma, com o cliente dono. O agente é liberado
   * por cliente, mas não pode responder por dois — sem cruzar os clientes a tela não tem como
   * saber que um agente livre aqui já está em uso em outro.
   */
  public async getRcsBrokerUsage(): Promise<RcsBrokerUsageI[]> {
    return await firstValueFrom(
      this.http.get<RcsBrokerUsageI[]>(this.chanelUrl + this.channelSlug + 'rcs/broker-usage')
    );
  }

  public async getAllCustomerChannels(body?: { page?: number; limit?: number; type?: string }): Promise<ChannelI[]> {
    const cachedChannels = await firstValueFrom(this.channelsCache$);
    if (cachedChannels === null) {
      //unloaded
      let params = new HttpParams().set('page', body?.page ?? 1).set('limit', body?.limit ?? 499);
      if (body?.type) {
        params = params.set('type', body?.type);
      }
      const channels = await firstValueFrom(
        this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug, { params })
      );

      this.channelsSubject.next(channels?.data ?? []);
      return channels?.data ?? [];
    }
    return cachedChannels;
  }

  public getWidgetChannelByCustomerId(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 5,
    active = 'createdAt'
  ): Observable<PageI<ChannelI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString());
    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + 'widget', { params });
  }

  public getWhatsappChannelByCustomerId(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 5,
    active = 'createdAt',
    status = StatusE.ACTIVE
  ): Observable<PageI<ChannelI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString())
      .set('status', status);
    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + 'whatsapp', { params });
  }

  public getWhatsappGroupChannelByCustomerId(): Observable<ChannelI[]> {
    return this.http.get<ChannelI[]>(this.chanelUrl + this.channelSlug + 'whatsapp-group');
  }

  public getGroupChannelsByCustomerId(): Observable<ChannelI[]> {
    const telegramParams = new HttpParams()
      .set('limit', '99999')
      .set('page', '1')
      .set('sort', 'desc')
      .set('active', 'createdAt')
      .set('status', StatusE.ACTIVE);

    return forkJoin([
      this.http.get<ChannelI[]>(this.chanelUrl + this.channelSlug + 'whatsapp-group'),
      this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + 'telegram', {
        params: telegramParams,
      }),
    ]).pipe(map(([whatsappGroups, telegramPage]) => [...(whatsappGroups ?? []), ...(telegramPage?.data ?? [])]));
  }

  public get customer() {
    return (this.userHelperService.getUserAttributes() as UserAttributesI)['custom:customer'];
  }

  public getWhatsappOficialChannelByCustomerId(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 5,
    active = 'createdAt'
  ): Observable<PageI<ChannelI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString());
    return this.http.get<PageI<ChannelI>>(
      this.chanelUrl + this.channelSlug + 'whatsapp-oficial?customer=' + this.customer,
      {
        params,
      }
    );
  }

  public getFacebookChannels(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 5,
    active = 'createdAt'
  ): Observable<PageI<ChannelI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString());
    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + 'facebook-messenger', { params });
  }

  public getInstagramChannels(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 5,
    active = 'createdAt'
  ): Observable<PageI<ChannelI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString());
    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + 'instagram-direct', { params });
  }

  public getLinkedinChannels(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 5,
    active = 'createdAt'
  ): Observable<PageI<ChannelI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString());
    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + 'linkedin', { params });
  }

  public async getEmailChannelsByCustomerIdCached(limit = 5) {
    return this.getChannelsByCustomerIdCachedByType(ChannelE.EMAIL, limit);
  }

  private async getChannelsByCustomerIdCachedByType(type: ChannelE, limit = 999) {
    const allChannels = await this.getAllCustomerChannels();
    const channels = allChannels.filter((channel) => channel.type === type).slice(0, limit);
    return channels;
  }

  public getEmailChannelsByCustomerId(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 5,
    active = 'createdAt',
    status = '',
    includeSes = false
  ): Observable<PageI<ChannelI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString())
      .set('includeSes', includeSes)
      .set('status', status);
    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + 'email', { params });
  }

  public getWhatsappCountByCustomerId(): Observable<number> {
    return this.http.get<number>(this.chanelUrl + this.channelSlug + 'whatsapp/count');
  }

  public getWhatsappOficialCountByCustomerId(): Observable<number> {
    return this.http.get<number>(
      this.chanelUrl + this.channelSlug + `whatsapp-oficial/count?customer=${this.customer}`
    );
  }

  public findAllWhatsappOficialByCustomerId(options?: any): Observable<PageI<ChannelI>> {
    let httpParams = new HttpParams().set('limit', options?.limit);

    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + `whatsapp-oficial`, {
      params: httpParams,
    });
  }

  public getWhatsAppChannelByCustomerId(): Observable<PageI<ChannelI>> {
    let httpParams = new HttpParams().set('limit', 0);

    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + 'whatsapp', {
      params: httpParams,
    });
  }
  public getWhatsAppChannelByCustomerIdCached(): Observable<ChannelI[]> {
    return from(this.getChannelsByCustomerIdCachedByType(ChannelE.WHATSAPP));
  }
  public getExternalChatChannelByCustomerIdCached(): Observable<ChannelI[]> {
    return from(this.getChannelsByCustomerIdCachedByType(ChannelE.EXTERNAL_CHAT));
  }
  public getWhatsappOficialChannelByCustomerIdCached(): Observable<ChannelI[]> {
    return from(this.getChannelsByCustomerIdCachedByType(ChannelE.WHATSAPP_OFICIAL));
  }
  public getFacebookCountByCustomerId(): Observable<number> {
    return this.http.get<number>(this.chanelUrl + this.channelSlug + 'facebook-messenger/count');
  }

  public getFacebookChannelByCustomerId(): Observable<PageI<ChannelI>> {
    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + 'facebook-messenger');
  }

  public getInstagramCountByCustomerId(): Observable<number> {
    return this.http.get<number>(this.chanelUrl + this.channelSlug + 'instagram-direct/count');
  }

  public getInstagramChannelByCustomerId(): Observable<PageI<ChannelI>> {
    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + 'instagram-direct');
  }

  public getWidgetCountByCustomerId(): Observable<number> {
    return this.http.get<number>(this.chanelUrl + this.channelSlug + 'widget/count');
  }

  public getChannelById(id: string): Observable<ChannelI> {
    return this.http.get<ChannelI>(this.chanelUrl + this.channelSlug + id);
  }

  public updateChannelById(id: string, channel: Partial<ChannelI>): Observable<ChannelI> {
    return this.http.patch<ChannelI>(this.chanelUrl + this.channelSlug + id, channel);
  }

  public deleteChannelById(id: string): Observable<ChannelI> {
    return this.http.delete<ChannelI>(this.chanelUrl + this.channelSlug + id);
  }

  public getTelegramChannelByCustomerId(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 5,
    active = 'createdAt',
    status = StatusE.ACTIVE
  ): Observable<PageI<ChannelI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString())
      .set('status', status);
    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + 'telegram', { params });
  }

  public getReclameAquiCountByCustomerId(): Observable<number> {
    return this.http.get<number>(this.chanelUrl + this.channelSlug + 'reclame-aqui/count');
  }

  public getReclameAquiChannels(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 5,
    active = 'createdAt'
  ): Observable<PageI<ChannelI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString());
    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + 'reclame-aqui', { params });
  }

  public getRcsChannelByCustomerId(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 5,
    active = 'createdAt'
  ): Observable<PageI<ChannelI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString());
    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug + 'rcs', {
      params,
    });
  }

  public getRcsCountByCustomerId(): Observable<number> {
    return this.http.get<number>(this.chanelUrl + this.channelSlug + `rcs/count`);
  }

  /**
   * MS Teams has no dedicated listing route in the `channels` service; the generic one
   * already filters by `type`, so there is nothing to add on the backend for the list.
   */
  public getMsTeamsChannels(
    search = '',
    sort = 'desc',
    page = 1,
    limit = 5,
    active = 'createdAt'
  ): Observable<PageI<ChannelI>> {
    const params = new HttpParams()
      .set('search', search)
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString())
      .set('type', ChannelE.MS_TEAMS);

    return this.http.get<PageI<ChannelI>>(this.chanelUrl + this.channelSlug, { params });
  }

  /**
   * Issues a new activation code and invalidates the previous one. Refused while the channel
   * is paired, so the screen only offers it on `pending` / `revoked`.
   */
  public renewMsTeamsActivationCode(id: string): Observable<ChannelI> {
    return this.http.post<ChannelI>(this.chanelUrl + this.channelSlug + `ms-teams/${id}/activation-code`, {});
  }

  /**
   * Unbinds the Azure AD tenant. Inbound stops reaching this channel until a new code is
   * issued and typed in the bot chat again.
   */
  public revokeMsTeamsPairing(id: string): Observable<ChannelI> {
    return this.http.delete<ChannelI>(this.chanelUrl + this.channelSlug + `ms-teams/${id}/pairing`);
  }
}
